/**
 * Formato único de CPF e CNPJ para EXIBIÇÃO (ClickUp 86akhub9e).
 *
 * Regra: conta só os dígitos. 11 dígitos viram `000.000.000-00`, 14 viram
 * `00.000.000/0000-00`. Qualquer outro comprimento é devolvido como veio.
 * Nulo ou vazio devolve "".
 *
 * O que este módulo NÃO faz, de propósito:
 * - não completa com zeros à esquerda (um valor curto não é um documento que
 *   perdeu zeros; em `organizations.federal_tax_id` os 92 CNPJs que começam com
 *   zero estão gravados com os 14 caracteres, conferido em 28/09/2026);
 * - não corta dígitos a mais (cortar transforma um valor errado noutro
 *   documento, que parece válido);
 * - não devolve placeholder do tipo `xxx.xxx.xxx-xx` (esconde o dado que a
 *   pessoa precisa ver para corrigir);
 * - não valida dígito verificador (validar é outra pergunta: ver `isValidCPF`
 *   e `isValidCNPJ`).
 *
 * Para máscara de digitação (formata parcial enquanto a pessoa escreve) use
 * `cpfInputMask` em `utils/cpf.ts`: é outra coisa.
 */

export type FederalTaxIdInput = string | number | null | undefined;

const CPF_LENGTH = 11;
const CNPJ_LENGTH = 14;

function asText(value: FederalTaxIdInput): string {
  if (value === null || value === undefined) return "";
  return String(value);
}

function digitsOf(text: string): string {
  return text.replace(/\D/g, "");
}

function maskCpfDigits(digits: string): string {
  return `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9, 11)}`;
}

function maskCnpjDigits(digits: string): string {
  return `${digits.slice(0, 2)}.${digits.slice(2, 5)}.${digits.slice(5, 8)}/${digits.slice(8, 12)}-${digits.slice(12, 14)}`;
}

/** `12345678901` -> `123.456.789-01`. Fora de 11 dígitos, devolve como veio. */
export function formatCpf(value: FederalTaxIdInput): string {
  const text = asText(value);
  const digits = digitsOf(text);
  return digits.length === CPF_LENGTH ? maskCpfDigits(digits) : text;
}

/** `12345678000190` -> `12.345.678/0001-90`. Fora de 14 dígitos, devolve como veio. */
export function formatCnpj(value: FederalTaxIdInput): string {
  const text = asText(value);
  const digits = digitsOf(text);
  return digits.length === CNPJ_LENGTH ? maskCnpjDigits(digits) : text;
}

/**
 * Para campos que podem trazer CPF ou CNPJ: decide pelo número de dígitos.
 * Quando o campo é sabidamente de CNPJ (ou de CPF), prefira `formatCnpj`
 * (ou `formatCpf`): um CNPJ com 11 dígitos é dado errado e deve aparecer como
 * veio, não com cara de CPF.
 */
export function formatFederalTaxId(value: FederalTaxIdInput): string {
  const text = asText(value);
  const digits = digitsOf(text);
  if (digits.length === CPF_LENGTH) return maskCpfDigits(digits);
  if (digits.length === CNPJ_LENGTH) return maskCnpjDigits(digits);
  return text;
}
